import { List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import React, { ReactNode, useEffect } from "react";
import { userNtfNewMenuPath, userNtfNewPackagesPath } from "../../../functions/src/admin/paths";
import { auth, schoolDatabase } from "../../api/firebase";
import { useDatabase } from "../../api/firebaseHooks";
import { useStrings } from "../../common-strings";
import { useNewsStrings } from "../news/news-strings";
import { useNewsUnread } from "../news/useNewsUnread";
import { useNavigationVisibility } from "../useNavigationVisibility";
import { profileHash } from "../profile/profileTabs";
import { useMonthlyMenuStrings } from "../userPackages/monthlyMenu-strings";
import { useAppLayoutStrings } from "./appLayout-strings";
import { usePlatformSupportStrings } from "../platform-support-strings";

type OrderNavItemProps = {
    selected: boolean;
    onClick: () => void;
    iconSrc: string;
    label: ReactNode;
    ariaLabel: string;
    badge?: ReactNode;
};

function orderNavAriaLabel(label: string, badgeAnnouncement?: string): string {
    return badgeAnnouncement ? `${label}, ${badgeAnnouncement}` : label;
}

function OrderNavItem({ selected, onClick, iconSrc, label, ariaLabel, badge }: OrderNavItemProps) {
    return (
        <ListItemButton
            selected={selected}
            disableRipple
            aria-current={selected ? "page" : undefined}
            aria-label={ariaLabel}
            onClick={onClick}
            className="order-app-nav__item"
            sx={{
                py: "4px",
                px: "8px",
                mb: "31px",
                borderRadius: "4px",
                maxWidth: "100%",
                gap: 0,
                alignItems: "center",
                "& .MuiListItemIcon-root": {
                    minWidth: 0,
                    marginRight: "17px",
                },
                "& .MuiListItemText-root": {
                    margin: 0,
                },
                "& .MuiListItemText-primary": {
                    fontSize: 18,
                    lineHeight: "24px",
                    letterSpacing: 0,
                },
            }}
        >
            <ListItemIcon className={selected ? "nav-icon selected" : "nav-icon"} sx={{ padding: "0 4px" }}>
                <img src={iconSrc} alt="" />
            </ListItemIcon>
            <ListItemText primary={label} aria-hidden />
            {badge}
        </ListItemButton>
    );
}

export default function Navigator({ selection, onChange }: NavigatorProps) {

    const commonStrings = useStrings();
    const layoutStrings = useAppLayoutStrings();
    const platformSupportStrings = usePlatformSupportStrings();
    const monthlyStrings = useMonthlyMenuStrings();
    const uid = auth.currentUser?.uid;
    const showTest = false;

    const { shouldShowPackages, shouldShowMonthlyMenu, shouldShowOrder } = useNavigationVisibility();
    const newsStrings = useNewsStrings();
    const { unreadCount: unreadNewsCount } = useNewsUnread(uid);
    const packagesNtf: boolean = useDatabase(uid ? userNtfNewPackagesPath(uid) : null)?.val();
    const menuNtf: boolean = useDatabase(uid ? userNtfNewMenuPath(uid) : null)?.val();

    useEffect(() => {
        if (!uid) return;
        if (selection === 'packages') {
            void schoolDatabase.ref(userNtfNewPackagesPath(uid)).set(true).catch((err) => console.warn('Could not clear packages nav notification flag:', err));
        } else if (selection === 'monthlyMenu') {
            void schoolDatabase.ref(userNtfNewMenuPath(uid)).set(true).catch((err) => console.warn('Could not clear monthly menu nav notification flag:', err));
        }
    }, [selection, uid]);

    return (
        <List component="nav" disablePadding className="order-app-nav" aria-label={layoutStrings.navMainLabel}>
            {shouldShowOrder && (
                <OrderNavItem
                    selected={selection === 'order'}
                    onClick={() => { onChange('order'); }}
                    iconSrc="/img-assets/order.svg"
                    label={layoutStrings.navOrder}
                    ariaLabel={layoutStrings.navOrder}
                />
            )}

            {shouldShowPackages && (
                <OrderNavItem
                    selected={selection === 'packages'}
                    onClick={() => { onChange('packages'); }}
                    iconSrc="/img-assets/packages.svg"
                    label={layoutStrings.navPackages}
                    ariaLabel={orderNavAriaLabel(layoutStrings.navPackages, !packagesNtf ? commonStrings.newBadge : undefined)}
                    badge={!packagesNtf ? <div className="ntf-box" aria-hidden="true">{commonStrings.newBadge}</div> : undefined}
                />
            )}

            {shouldShowMonthlyMenu && (
                <OrderNavItem
                    selected={selection === 'monthlyMenu'}
                    onClick={() => { onChange('monthlyMenu'); }}
                    iconSrc="/img-assets/menu.svg"
                    label={monthlyStrings.navLabel}
                    ariaLabel={orderNavAriaLabel(monthlyStrings.navLabel, !menuNtf ? commonStrings.newBadge : undefined)}
                    badge={!menuNtf ? <div className="ntf-box" aria-hidden="true">{commonStrings.newBadge}</div> : undefined}
                />
            )}

            {/* <li className={selection === 'weeklyOrders' && 'selected' || ''} style={{ display: 'flex', flexFlow: 'row nowrap' }} onClick={() => { onChange('weeklyOrders') }}>
            <div className={selection === 'profile' && 'nav-icon selected' || 'nav-icon'} style={{ padding: '0 4px' }}>
                <img src="/img-assets/account.svg" />
            </div>
            <Lang>Παραγγελίες Εβδομάδας|Weekly Orders</Lang>
        </li> */}

            <OrderNavItem
                selected={selection === 'news'}
                onClick={() => { onChange('news'); }}
                iconSrc="/img-assets/news.svg"
                label={newsStrings.navLabel}
                ariaLabel={orderNavAriaLabel(
                    newsStrings.navLabel,
                    unreadNewsCount > 0 ? newsStrings.navUnreadBadge(unreadNewsCount) : undefined,
                )}
                badge={unreadNewsCount > 0 ? <div className="ntf-box ntf-box-count" aria-hidden="true">{unreadNewsCount}</div> : undefined}
            />

            <OrderNavItem
                selected={selection === 'profile'}
                onClick={() => { onChange(profileHash('personal')); }}
                iconSrc="/img-assets/account.svg"
                label={commonStrings.navMyAccount}
                ariaLabel={commonStrings.navMyAccount}
            />

            <OrderNavItem
                selected={selection === 'settings'}
                onClick={() => { onChange('settings'); }}
                iconSrc="/img-assets/settings.svg"
                label={commonStrings.navSettings}
                ariaLabel={commonStrings.navSettings}
            />

            <OrderNavItem
                selected={selection === 'platformSupport'}
                onClick={() => { onChange('platformSupport'); }}
                iconSrc="/img-assets/platform-support.svg"
                label={platformSupportStrings.navLabel}
                ariaLabel={platformSupportStrings.navLabel}
            />

            {showTest && (
                <OrderNavItem
                    selected={selection === 'test'}
                    onClick={() => { onChange('test'); }}
                    iconSrc="/img-assets/settings.svg"
                    label={layoutStrings.navTest}
                    ariaLabel={layoutStrings.navTest}
                />
            )}
        </List>
    );
}

type NavigatorProps = {
    selection: NavigatorOption
    onChange: (value: NavigatorOption) => void
}

export type NavigatorOption = 'profile' | 'order' | 'packages' | 'monthlyMenu' | 'feedback' | 'settings' | 'test' | 'paySuccess' | string;
